// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch, type ApiError } from './client.js';
import type {
  AttachmentPresignBody,
  AttachmentPresignResponse,
  AttachmentReadyBody,
  DraftBody,
  DraftResult,
  IntakeBundle,
  PrefillItem,
  SubmitResult,
} from './types.js';

export const intakeQueryKey = (token: string) => ['intake', token] as const;

export function useIntake(token: string) {
  return useQuery<IntakeBundle>({
    queryKey: intakeQueryKey(token),
    queryFn: () => apiFetch<IntakeBundle>(`/api/intake/${encodeURIComponent(token)}`),
    staleTime: Infinity,
  });
}

/**
 * Confirm the patient's identity to unlock EMR demographics
 * (intakeform-preview-200.7.9). The response carries the prefill; nothing
 * from the chart arrives before this succeeds.
 */
export function useVerifyIdentity(token: string) {
  return useMutation<{ prefill: Record<string, PrefillItem> }, ApiError, { birthDate: string }>({
    mutationFn: (body) =>
      apiFetch(`/api/intake/${encodeURIComponent(token)}/verify-identity`, {
        method: 'POST',
        body,
      }),
  });
}

export function useSaveDraft(token: string) {
  return useMutation<DraftResult, ApiError, DraftBody>({
    mutationFn: (body) =>
      apiFetch(`/api/intake/${encodeURIComponent(token)}/draft`, {
        method: 'POST',
        body,
      }),
  });
}

export function useSubmitResponse(token: string) {
  return useMutation<SubmitResult, ApiError, DraftBody>({
    mutationFn: (body) =>
      apiFetch(`/api/intake/${encodeURIComponent(token)}/submit`, {
        method: 'POST',
        body,
      }),
  });
}

export interface AttachmentUploadProgress {
  attachmentId: string;
  url: string;
}

/**
 * Implements the attachment sub-flow per PLAN.md §6.1:
 *   1. POST /api/intake/:token/attachments to mint a presigned PUT URL
 *   2. PUT the bytes directly to that URL (S3 / R2 / …)
 *   3. POST /api/intake/:token/attachments/:id/ready to transition the row
 *
 * The API never sees the bytes — they go directly to object storage.
 */
export async function uploadAttachment(
  token: string,
  meta: AttachmentPresignBody,
  blob: Blob,
  sha256?: string,
): Promise<AttachmentUploadProgress> {
  const presign = await apiFetch<AttachmentPresignResponse>(
    `/api/intake/${encodeURIComponent(token)}/attachments`,
    { method: 'POST', body: meta },
  );

  const putRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'content-type': meta.contentType },
  });
  if (!putRes.ok) {
    throw new Error(`upload failed: ${putRes.status} ${putRes.statusText}`);
  }

  const ready: AttachmentReadyBody = sha256 !== undefined ? { sha256 } : {};
  await apiFetch<{ ok: true }>(
    `/api/intake/${encodeURIComponent(token)}/attachments/${encodeURIComponent(presign.attachmentId)}/ready`,
    { method: 'POST', body: ready },
  );

  return {
    attachmentId: presign.attachmentId,
    url: `/api/attachments/${encodeURIComponent(presign.attachmentId)}/download`,
  };
}

export function useUploadAttachment(token: string) {
  return useMutation<
    AttachmentUploadProgress,
    Error,
    { meta: AttachmentPresignBody; blob: Blob; sha256?: string }
  >({
    mutationFn: ({ meta, blob, sha256 }) => uploadAttachment(token, meta, blob, sha256),
  });
}
